import { useEffect, useRef } from 'react';
import type { MixTrack } from './types';
import { computeDriftAdjustedRate } from '../../timeStretch';

export function useSyncController(
  enabled: boolean,
  tracks: MixTrack[],
  elsRef: React.MutableRefObject<Record<string, HTMLAudioElement | null>>,
  masterPlaybackRate: number,
  onSeekPingRef?: React.MutableRefObject<number>,
) {
  const rafRef = useRef<number | null>(null);
  const lastSyncAtRef = useRef(0);
  const smoothedRatesRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!enabled || tracks.length < 2) { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); rafRef.current = null; return; }
    const getLeader = () => {
      let best: HTMLAudioElement | null = null;
      let maxT = -Infinity;
      for (const t of tracks) {
        const el = elsRef.current[t.role];
        const ct = el ? (el.currentTime || 0) : -Infinity;
        if (ct > maxT) { maxT = ct; best = el || null; }
      }
      return best;
    };

    const maxAdjust = 0.015;    // ±1.5%
    const hardReset = 0.02;     // 20 ms
    const kP = 0.35;            // stronger
    const updateIntervalMs = 16; // ~60 Hz
    const alpha = 0.85;         // smoothing

    const tick = () => {
  const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const cooldownMs = 600;
      if (now - lastSyncAtRef.current >= updateIntervalMs && (!onSeekPingRef || now - (onSeekPingRef.current || 0) >= cooldownMs)) {
        lastSyncAtRef.current = now;
        const leader = getLeader();
        if (leader) {
          for (let i = 0; i < tracks.length; i++) {
            const role = tracks[i].role; const el = elsRef.current[role]; if (!el || el.paused || el === leader) continue;
            const err = (el.currentTime || 0) - (leader.currentTime || 0); if (!isFinite(err)) continue;
            if (Math.abs(err) > hardReset) {
              try { el.currentTime = leader.currentTime; el.playbackRate = masterPlaybackRate; smoothedRatesRef.current[role] = masterPlaybackRate; } catch {}
            } else {
              if (Math.abs(err) < 0.003) { /* deadband 3 ms */ }
              else {
                try {
                  const target = computeDriftAdjustedRate(masterPlaybackRate, err, { maxAdjust, kP });
                  const prev = smoothedRatesRef.current[role] ?? el.playbackRate ?? masterPlaybackRate;
                  const next = prev * alpha + target * (1 - alpha);
                  if (Math.abs(next - prev) >= 0.003) { el.playbackRate = next; smoothedRatesRef.current[role] = next; }
                } catch {}
              }
            }
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); rafRef.current = null; };
  }, [enabled, tracks, elsRef, masterPlaybackRate, onSeekPingRef]);
}
